/** @module What the scan worker is doing: throughput, decisions, where the time goes, failures. */
import { useTranslation } from "react-i18next";

import { count } from "@/lib/format";
import { useScanHealth } from "@/features/admin/use-admin";

function Tile({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="flex flex-col gap-1 panel panel-lg">
      <span className="t-xs t-muted">{label}</span>
      <span className="num text-[length:var(--text-xl)] font-semibold" style={{ color: tone }}>
        {value}
      </span>
    </div>
  );
}

export function ScansPanel() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const health = useScanHealth();

  if (health.isPending) return <p className="t-muted">{t("common.loading")}</p>;
  if (health.isError)
    return (
      <p role="alert" className="loss">
        {t("common.loadError")}
      </p>
    );

  const data = health.data;
  const decided = data.decided_auto + data.decided_review + data.decided_no_match;
  const autoShare = decided > 0 ? Math.round((data.decided_auto / decided) * 100) : 0;

  return (
    <div className="flex flex-col gap-6">
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label={t("admin.scans.lastDay")} value={count(data.last_day, locale)} />
        <Tile label={t("admin.scans.open")} value={count(data.open, locale)} />
        <Tile
          label={t("admin.scans.failed")}
          value={count(data.failed, locale)}
          tone={data.failed > 0 ? "var(--loss)" : undefined}
        />
        <Tile label={t("admin.scans.autoShare")} value={`${String(autoShare)} %`} />
      </section>

      <section className="flex flex-col gap-2 panel panel-lg">
        <h3 className="t-h3">{t("admin.scans.decisions")}</h3>
        <dl className="grid gap-x-6 gap-y-1 text-[length:var(--text-sm)] sm:grid-cols-3">
          {(
            [
              ["decided_auto", data.decided_auto],
              ["decided_review", data.decided_review],
              ["decided_no_match", data.decided_no_match],
            ] as const
          ).map(([key, value]) => (
            <div key={key} className="flex justify-between gap-3">
              <dt className="t-muted">{t(`admin.scans.${key}`)}</dt>
              <dd className="num">{count(value, locale)}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="panel panel-lg">
        <h3 className="mb-2 t-h3">{t("admin.scans.stages")}</h3>
        {data.stages.length === 0 ? (
          <p className="t-muted">{t("admin.scans.noStages")}</p>
        ) : (
          <table className="w-full text-[length:var(--text-sm)]">
            <thead>
              <tr className="text-left text-(--text-muted)">
                <th className="py-1">{t("admin.scans.stage")}</th>
                <th className="py-1 text-right">{t("admin.scans.median")}</th>
                <th className="py-1 text-right">{t("admin.scans.p95")}</th>
                <th className="py-1 text-right">{t("admin.scans.runs")}</th>
              </tr>
            </thead>
            <tbody>
              {data.stages.map((stage) => (
                <tr key={stage.stage} className="border-t border-(--border)">
                  <td className="py-1">
                    {t(`admin.scans.stageNames.${stage.stage}`, stage.stage)}
                  </td>
                  <td className="num py-1 text-right">{Math.round(stage.p50_ms)} ms</td>
                  <td className="num py-1 text-right">{Math.round(stage.p95_ms)} ms</td>
                  <td className="num py-1 text-right">{count(stage.runs, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {data.recent_failures.length > 0 ? (
        <section className="flex flex-col gap-2 panel panel-lg">
          <h3 className="t-h3">{t("admin.scans.failures")}</h3>
          <ul className="flex flex-col gap-2 text-[length:var(--text-sm)]">
            {data.recent_failures.map((failure, index) => (
              <li key={index} className="border-t border-(--border) pt-2 first:border-0 first:pt-0">
                <span className="t-muted">
                  {typeof failure.created_at === "string"
                    ? new Date(failure.created_at).toLocaleString(locale)
                    : ""}
                </span>
                <p className="break-words text-(--loss)">
                  {typeof failure.error === "string" ? failure.error : ""}
                </p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
